import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as S from "./styles";

import FormButtons from "../FormButtons";
import ImgPreviewCard from "../ImgPreviewCard";

import { api } from "../../services/api";
import {
  fireSuccessMsg,
  fireErrorMsg,
  onRequestError,
} from "../../services/functions";
import { useLoading } from "../../contexts/LoadingContext";

import Swal from "sweetalert2";
import { FileUploader } from "react-drag-drop-files";

const CarouselSlideForm = ({
  carouselSlide,
  allSlides,
  products,
  defaultPage,
}) => {
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [productId, setProductId] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [linkLabel, setLinkLabel] = useState("");
  const [pagesToShow, setPagesToShow] = useState(defaultPage || "");
  const [order, setOrder] = useState(0);
  const [previewImage, setPreviewImage] = useState();
  const [file, setFile] = useState();
  const [additionalFiles, setAdditionalFiles] = useState([]);
  const [additionalPreviews, setAdditionalPreviews] = useState([]);

  const navigate = useNavigate();
  const { setLoading } = useLoading();

  useEffect(() => {
    if (carouselSlide) {
      // console.log("TESTE carouselSlide", carouselSlide);
      setName(carouselSlide.name);
      setText(carouselSlide.text);
      setProductId(carouselSlide.product_id);
      setLinkUrl(carouselSlide.link_url);
      setLinkLabel(carouselSlide.link_label);
      setPagesToShow(carouselSlide.pages_to_show || "");
      setOrder(carouselSlide.order);
      setPreviewImage(carouselSlide.image);
      if (carouselSlide.images) {
        setAdditionalPreviews(carouselSlide.images);
      }
    }
  }, [carouselSlide]);

  const backToList = () => {
    navigate("/carousel-slides");
  };

  const refreshPage = () => {
    navigate(0);
  };

  const onSuccess = (msg, isEdit) => {
    const cb = isEdit ? backToList : refreshPage;

    fireSuccessMsg(msg, cb);
    setName("");
    setText("");
    setProductId("");
    setLinkUrl("");
    setLinkLabel("");
    setPagesToShow("");
    setOrder(0);
    setPreviewImage();
    setFile();
    setAdditionalFiles([]);
    setAdditionalPreviews([]);
  };

  const createNewCarouselSlide = async () => {
    const formData = new FormData();
    formData.append("name", name);
    formData.append("text", text);
    formData.append("order", order);
    if (productId) formData.append("product_id", productId);
    if (linkUrl) formData.append("link_url", linkUrl);
    if (linkLabel) formData.append("link_label", linkLabel);
    if (pagesToShow) formData.append("pages_to_show", pagesToShow);
    if (file) formData.append("image", file);

    additionalFiles.forEach(f => {
      formData.append("additional_images[]", f);
    });

    await api
      .post("/api/carousel-slides", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then(response => onSuccess(response.data.msg))
      .catch(e => {
        if (!e.caught) {
          e.msg = "Algo deu errado ao cadastrar o slide!";
        }
        onRequestError(e.msg, e);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const editCarouselSlide = async () => {
    const formData = new FormData();
    formData.append("_method", "PATCH");
    formData.append("name", name);
    formData.append("text", text);
    formData.append("order", order);
    if (productId) formData.append("product_id", productId);
    if (linkUrl) formData.append("link_url", linkUrl);
    if (linkLabel) formData.append("link_label", linkLabel);
    if (pagesToShow) formData.append("pages_to_show", pagesToShow);
    if (file) formData.append("image", file);

    additionalFiles.forEach(f => {
      formData.append("additional_images[]", f);
    });

    await api
      .post(`/api/carousel-slides/${carouselSlide.id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then(response => onSuccess(response.data.msg, true))
      .catch(e => {
        if (!e.caught) {
          e.msg = "Algo deu errado ao editar o slide!";
        }
        onRequestError(e.msg, e);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const submitHandler = e => {
    e.preventDefault();
    setLoading(true);
    if (carouselSlide) {
      editCarouselSlide();
    } else {
      createNewCarouselSlide();
    }
  };

  const removeImgPreview = () => {
    if (previewImage.originalFile) {
      removeFile(previewImage.originalFile);
    }

    setPreviewImage();
  };

  const createImgPreview = result => {
    const preview = {
      id: null,
      path: result.data,
      originalFile: result.originalFile,
    };
    setPreviewImage(preview);
  };

  const readFile = file => {
    return new Promise(resolve => {
      let reader = new FileReader();
      reader.onload = () =>
        resolve({ data: reader.result, originalFile: file });
      reader.readAsDataURL(file);
    });
  };

  const onFileUpload = newFile => {
    readFile(newFile).then(response => createImgPreview(response));
    setFile(newFile);
  };

  const onAdditionalFilesUpload = newFiles => {
    const filesArray = Array.from(newFiles);
    setAdditionalFiles([...additionalFiles, ...filesArray]);

    const previewPromises = filesArray.map(f => readFile(f));
    Promise.all(previewPromises).then(responses => {
      const newPreviews = responses.map(r => ({
        id: null,
        path: r.data,
        originalFile: r.originalFile,
      }));
      setAdditionalPreviews([...additionalPreviews, ...newPreviews]);
    });
  };

  const removeFile = file => {
    setFile();
  };

  const removeAdditionalFile = file => {
    const copy = [...additionalFiles];
    const index = copy.indexOf(file);
    copy.splice(index, 1);
    setAdditionalFiles(copy);
  };

  const removeAdditionalPreview = preview => {
    const copy = [...additionalPreviews];
    const index = copy.indexOf(preview);
    copy.splice(index, 1);
    setAdditionalPreviews(copy);

    if (preview.originalFile) {
      removeAdditionalFile(preview.originalFile);
    }
  };

  const showDeleteMsg = id => {
    Swal.fire({
      icon: "warning",
      title: "Você tem certeza de que quer excluir a imagem salva?",
      showDenyButton: true,
      confirmButtonText: "Sim, excluir",
      denyButtonText: `Cancelar`,
    }).then(result => {
      if (result.isConfirmed) {
        deleteFromStorage(id);
      }
    });
  };

  const showDeleteAdditionalMsg = preview => {
    if (!preview.id) {
      removeAdditionalPreview(preview);
      return;
    }

    Swal.fire({
      icon: "warning",
      title: "Você tem certeza de que quer excluir esta imagem da galeria?",
      showDenyButton: true,
      confirmButtonText: "Sim, excluir",
      denyButtonText: `Cancelar`,
    }).then(result => {
      if (result.isConfirmed) {
        deleteAdditionalFromStorage(preview);
      }
    });
  };

  const deleteFromStorage = async id => {
    setLoading(true);
    await api
      .delete(`/api/carousel-slides/${carouselSlide.id}/image`) // Adjusted to match potential API
      .then(response => {
        fireSuccessMsg(response.data.msg);
        removeImgPreview();
      })
      .catch(e => {
        if (!e.caught) {
          e.msg = "Algo deu errado ao excluir a imagem!";
        }
        onRequestError(e.msg, e);
      })
      .finally(() => setLoading(false));
  };

  const deleteAdditionalFromStorage = async preview => {
    setLoading(true);
    await api
      .delete(`/api/carousel-slide-images/${preview.id}`)
      .then(response => {
        fireSuccessMsg(response.data.msg);
        removeAdditionalPreview(preview);
      })
      .catch(e => {
        if (!e.caught) {
          e.msg = "Algo deu errado ao excluir a imagem!";
        }
        onRequestError(e.msg, e);
      })
      .finally(() => setLoading(false));
  };

  return (
    <S.CarouselSlideFormWrapper onSubmit={submitHandler}>
      <div style={{ marginBottom: "2rem", padding: "1rem", background: "#f0f4f8", borderRadius: "8px", border: "1px solid #d1d9e0" }}>
        <p style={{ margin: 0, fontWeight: "600", color: "#1a365d", fontSize: "0.875rem", display: "flex", alignItems: "center" }}>
          <span className="material-symbols-rounded" style={{ marginRight: "0.5rem", fontSize: "1.25rem" }}>info</span>
          Este slide aparecerá em: <strong style={{ marginLeft: "0.5rem", color: "#2d3748" }}>{pagesToShow === "/" ? "Página Inicial (Home)" : pagesToShow === "/produtos" ? "Página de Produtos" : pagesToShow || "Todas as páginas"}</strong>
        </p>
      </div>
      <div>
        <label htmlFor="inp_name">Título</label>
        <input
          id="inp_name"
          type="text"
          placeholder="Título do slide"
          value={name}
          onChange={e => setName(e.target.value)}
          required
        />
      </div>

      <div>
        <label htmlFor="inp_text">Texto</label>
        <textarea
          id="inp_text"
          placeholder="Texto do slide"
          value={text}
          onChange={e => setText(e.target.value)}
          required
        />
      </div>

      <div>
        <label htmlFor="inp_order">Ordem</label>
        <select
          id="inp_order"
          name="order"
          value={order}
          onChange={e => setOrder(e.target.value)}
          required
        >
          <option value={0}>0</option>
          {allSlides?.map((slide, i) => (
            <option value={i + 1}>{i + 1}</option>
          ))}
        </select>
      </div>

      {products?.length > 0 && (
        <div>
          <label htmlFor="inp_product_id">Produto (opcional)</label>
          <select
            id="inp_product_id"
            name="product_id"
            value={productId}
            onChange={e => setProductId(e.target.value)}
          >
            <option value={""}>Nenhum</option>
            {products.map((prod, i) => (
              <option key={i} value={prod.id}>
                {`${prod.name} ${prod.width} x ${prod.length}`}
              </option>
            ))}
          </select>
        </div>
      )}

      <div>
        <label htmlFor="inp_name">URL para link (opcional)</label>
        <input
          id="inp_link"
          type="text"
          placeholder="URL do link"
          value={linkUrl}
          onChange={e => setLinkUrl(e.target.value)}
        />
      </div>

      <div>
        <label htmlFor="inp_name">Texto para link (opcional)</label>
        <input
          id="inp_link_label"
          type="text"
          placeholder="Texto do link"
          value={linkLabel}
          onChange={e => setLinkLabel(e.target.value)}
        />
      </div>

      <div>
        <label htmlFor="inp_name">Páginas (opcional)</label>
        <input
          id="inp_pages"
          type="text"
          placeholder="URL das páginas, separadas por vírgula"
          value={pagesToShow}
          onChange={e => setPagesToShow(e.target.value)}
        />
      </div>

      <div>
        <p>Imagem principal do slide</p>
        <div style={{ marginTop: "0.5rem" }}>
          {previewImage ? (
            <ImgPreviewCard
              id={previewImage.id}
              path={previewImage.path}
              localDeleteHandler={removeImgPreview}
              remoteDeleteHandler={showDeleteMsg}
            />
          ) : (
            <p>Este slide não possui imagem principal</p>
          )}
        </div>
      </div>

      <FileUploader
        maxSize={2}
        types={["jpg", "jpeg", "png", "webp"]}
        classes="file-uploader"
        label="Solte a imagem principal aqui..."
        handleChange={onFileUpload}
      />

      <div style={{ marginTop: "2rem" }}>
        <p>Fotos Adicionais (Galeria)</p>
        <div style={{ 
          display: "grid", 
          gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", 
          gap: "1rem",
          marginTop: "1rem" 
        }}>
          {additionalPreviews.map((preview, i) => (
            <ImgPreviewCard
              key={i}
              id={preview.id}
              path={preview.image_path || preview.path}
              localDeleteHandler={() => removeAdditionalPreview(preview)}
              remoteDeleteHandler={() => showDeleteAdditionalMsg(preview)}
            />
          ))}
        </div>
      </div>

      <FileUploader
        multiple
        maxSize={2}
        types={["jpg", "jpeg", "png", "webp"]}
        classes="file-uploader"
        label="Solte fotos adicionais aqui para criar o bloco..."
        handleChange={onAdditionalFilesUpload}
      />

      <FormButtons cancelCallback={backToList} />
    </S.CarouselSlideFormWrapper>
  );
};

export default CarouselSlideForm;
